import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import publicPages from '../data/publicPages.json'
import { isSupportedLanguage, SUPPORTED_LANGUAGES } from '../i18n'

const SITE_URL = 'https://intraveo.com'
const OG_IMAGE = `${SITE_URL}/og-image.png`

// OG locale codes, matching the markets in the CLAUDE.md rollout order
// (Belgium/Netherlands first, then Germany, Italy, France, China).
const OG_LOCALES: Record<string, string> = {
  en: 'en_US',
  nl: 'nl_BE',
  fr: 'fr_BE',
  de: 'de_DE',
  it: 'it_IT',
  zh: 'zh_CN',
  es: 'es_ES',
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setCanonical(href: string) {
  let el = document.head.querySelector('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function removeCanonical() {
  document.head.querySelector('link[rel="canonical"]')?.remove()
}

function removeHreflangLinks() {
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove())
}

function addHreflangLink(hreflang: string, href: string) {
  const el = document.createElement('link')
  el.setAttribute('rel', 'alternate')
  el.setAttribute('hreflang', hreflang)
  el.setAttribute('href', href)
  document.head.appendChild(el)
}

/**
 * Per-route document.title, meta description, canonical URL, hreflang
 * alternates and Open Graph / Twitter Card tags. Same direct-DOM approach
 * App.tsx used to use for the site-wide title/description, just driven by
 * the current :lang + page instead of language alone. Mounted once in
 * LangLayout, so it re-runs on every route change.
 */
function SeoHead() {
  const { t } = useTranslation()
  const { lang } = useParams()
  const location = useLocation()

  useEffect(() => {
    if (!lang || !isSupportedLanguage(lang)) return

    // Path segment after /:lang/, '' for the language root.
    const path = location.pathname.replace(new RegExp(`^/${lang}/?`), '')
    const page = publicPages.find((p) => p.path === path)

    if (!page) {
      // Unlisted route: a genuine 404, kept out of the index, no
      // canonical/hreflang/OG for a page that should never be shared or
      // indexed. This is always the wildcard NotFoundPage.
      document.title = t('notFound.metaTitle')
      setMeta('name', 'robots', 'noindex, nofollow')
      removeCanonical()
      removeHreflangLinks()
      return
    }

    const title = t(`meta.pages.${page.metaKey}.title`)
    const description = t(`meta.pages.${page.metaKey}.description`)
    const canonicalPath = page.path ? `/${page.path}` : '/'
    const canonicalUrl = `${SITE_URL}/${lang}${canonicalPath}`

    document.title = title
    setMeta('name', 'description', description)
    setMeta('name', 'robots', 'index, follow')
    setCanonical(canonicalUrl)

    setMeta('property', 'og:site_name', 'Intraveo')
    setMeta('property', 'og:type', 'website')
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', canonicalUrl)
    setMeta('property', 'og:image', OG_IMAGE)
    setMeta('property', 'og:image:width', '1200')
    setMeta('property', 'og:image:height', '630')
    setMeta('property', 'og:image:alt', t('meta.title'))
    setMeta('property', 'og:locale', OG_LOCALES[lang] ?? 'en_US')

    setMeta('name', 'twitter:card', 'summary_large_image')
    setMeta('name', 'twitter:title', title)
    setMeta('name', 'twitter:description', description)
    setMeta('name', 'twitter:image', OG_IMAGE)

    // Self-referencing + every other language, per Google's hreflang
    // guidance, plus x-default pointing at the English version.
    removeHreflangLinks()
    for (const otherLang of SUPPORTED_LANGUAGES) {
      addHreflangLink(otherLang, `${SITE_URL}/${otherLang}${canonicalPath}`)
    }
    addHreflangLink('x-default', `${SITE_URL}/en${canonicalPath}`)
  }, [lang, location.pathname, t])

  return null
}

export default SeoHead
